import { action, computed, makeObservable, observable } from 'mobx'

import {
  IWorkflowStep,
  PermitFieldType,
  WorkflowStepType,
} from '~/client/graph'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import PermitInspection from '~/client/src/shared/models/PermitInspection'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'

import PermitFieldsBaseStore, {
  IPermitFormField,
} from './PermitFieldsBase.store'
import PermitWorkflowFieldsStore from './PermitWorkflowFields.store'

export default class PermitInspectionFieldsStore
  extends PermitFieldsBaseStore
  implements IPermitFieldsStore
{
  @observable public isNewInspection: boolean = false
  @observable public inspectionDate: Date = null
  @observable public editableEntity: PermitInspection = null
  @observable public existingEntity: PermitInspection = null

  public constructor(
    private readonly eventsStore: EventsStore,
    private readonly permitWorkflowFieldsStore: PermitWorkflowFieldsStore,
    locationStore: LocationAttributesStore,
  ) {
    super(eventsStore.appState, locationStore)

    makeObservable(this)
  }

  @computed
  public get emptyInspection(): PermitInspection {
    const { id: userId } = this.eventsStore.appState.user
    const { id: projectId } = this.eventsStore.appState.activeProject

    return new PermitInspection(
      null,
      this.permitWorkflowFieldsStore.editableEntity.id,
      projectId,
      userId,
      null,
      [],
      this.inspectionDate?.getTime(),
    )
  }

  protected get canIgnoreValidation(): boolean {
    return false
  }

  @computed
  public get currentStepFieldIds(): Set<string> {
    const [currentStep] = this.workflowSteps
    const currentStepFieldIds = currentStep?.fields?.map(({ id }) => id) || []
    const currentStepCondFieldIds =
      currentStep?.conditionalFields?.flatMap(({ values }) =>
        values.map(v => v.id),
      ) || []

    return new Set(currentStepFieldIds.concat(currentStepCondFieldIds))
  }

  @computed
  protected get fieldIdsToIgnore(): string[] {
    return []
  }

  @computed
  public get previousStepsFields(): IPermitFormField[] {
    return this.fields
  }

  @computed
  public get firstTwoStepsFieldIds(): Set<string> {
    return new Set(this.fields.map(({ id }) => id))
  }

  @computed
  public get fields(): IPermitFormField[] {
    const { fields } = this.template?.recurringInspectionStep || {}

    return fields?.reduce((fieldsList, typeField) => {
      if (!typeField.isShown) {
        return fieldsList
      }

      fieldsList.push(this.getFormFieldModel(typeField))

      if (typeField.type === PermitFieldType.Table) {
        fieldsList.push(...(this.getTableFieldModels(typeField) || []))
      }

      fieldsList.push(...this.getConditionalFormFields(typeField))

      return fieldsList
    }, [] as IPermitFormField[])
  }

  @computed
  protected get workflowSteps(): IWorkflowStep[] {
    if (!this.template) {
      return []
    }
    return this.template.workflowSteps.filter(
      s => s.type === WorkflowStepType.RecurringInspection,
    )
  }

  @action.bound
  public selectInspection(inspection: PermitInspection, date: Date) {
    if (!this.permitWorkflowFieldsStore.editableEntity?.id) {
      return
    }

    this.editableEntity = inspection?.permitId
      ? inspection.copy()
      : this.emptyInspection

    if (!this.editableEntity.inspectionDate) {
      this.editableEntity.inspectionDate = date.getTime()
    }

    if (inspection?.id) {
      this.existingEntity = inspection
    }

    this.isNewInspection = !inspection
    this.inspectionDate = date
  }

  @action.bound
  public setEmptyInspection() {
    this.editableEntity = this.emptyInspection
  }

  @action.bound
  public deselectInspection() {
    this.isNewInspection = false
    this.inspectionDate = null
    this.editableEntity = null
    this.existingEntity = null
  }

  @computed
  private get template(): WorkflowType {
    return this.permitWorkflowFieldsStore.template
  }
}
